import React, { useState, useEffect } from 'react';
import { X, Search, Scissors, Package, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { fetchInventarioActual } from '@/services/movimientosLenceria.service';
import { crearCorte } from '@/services/cortesInventario.service';

const CrearCorteModal = ({ isOpen, onClose, onSuccess, user }) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [prendas, setPrendas] = useState([]);
  const [conteos, setConteos] = useState({}); // { [tipo_prenda_id]: cantidad_real }
  const [notas, setNotas] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadPrendas();
      setNotas('');
      setSearchTerm('');
    }
  }, [isOpen]);

  const loadPrendas = async () => {
    setIsLoading(true);
    try {
      const response = await fetchInventarioActual({ page: 1, limit: 2000 });
      const items = response.inventario || [];
      setPrendas(items);
      // Prellenar el conteo real con el disponible del sistema
      const initial = {};
      items.forEach((p) => {
        const id = p.tipo_prenda?.id;
        if (id) initial[id] = p.cantidad_disponible;
      });
      setConteos(initial);
    } catch (error) {
      console.error('Error al cargar prendas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las prendas',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeConteo = (prendaId, value) => {
    const num = value === '' ? '' : Number(value);
    setConteos((prev) => ({ ...prev, [prendaId]: num }));
  };

  const filteredPrendas = prendas.filter((p) =>
    p.tipo_prenda?.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async () => {
    const confirmar = window.confirm(
      '¿Generar corte de inventario?\n\n' +
      'Esta acción:\n' +
      '• Guarda el inventario actual y sus movimientos en el corte.\n' +
      '• ARCHIVA y PURGA los movimientos del período actual.\n' +
      '• Reemplaza el inventario disponible con el conteo real ingresado.\n\n' +
      'Esta operación no se puede deshacer.'
    );
    if (!confirmar) return;

    setIsSaving(true);
    try {
      const conteosArray = prendas.map((p) => {
        const id = p.tipo_prenda?.id;
        const valor = conteos[id];
        return {
          tipo_prenda_id: id,
          cantidad_real: valor === '' || valor == null ? 0 : Number(valor)
        };
      });

      const response = await crearCorte({
        usuario_id: user?.id,
        usuario_nombre: user?.name,
        notas,
        conteos: conteosArray
      });

      toast({
        title: 'Corte generado',
        description: response?.message || 'El corte se generó correctamente'
      });
      onSuccess();
    } catch (error) {
      console.error('Error al generar corte:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo generar el corte',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-indigo-50 border-b border-indigo-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Scissors className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Generar Corte de Inventario</h2>
              <p className="text-sm text-gray-600">
                Ingresa el conteo físico real. El sistema registrará las diferencias.
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Notas + búsqueda */}
        <div className="px-6 py-4 border-b space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-700">Notas del corte</label>
            <Input
              type="text"
              placeholder="Observaciones de este corte (opcional)"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Buscar prenda..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Tabla de conteo */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-indigo-600 animate-spin" />
            </div>
          ) : filteredPrendas.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">
                {searchTerm ? 'No se encontraron prendas' : 'No hay prendas en el inventario'}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b sticky top-0 bg-white">
                <tr className="text-left text-gray-600">
                  <th className="py-2">Prenda</th>
                  <th className="py-2 text-center">Disponible (sistema)</th>
                  <th className="py-2 text-center">Conteo real</th>
                  <th className="py-2 text-center">Diferencia</th>
                </tr>
              </thead>
              <tbody>
                {filteredPrendas.map((p) => {
                  const id = p.tipo_prenda?.id;
                  const sistema = p.cantidad_disponible;
                  const real = conteos[id];
                  const realNum = real === '' || real == null ? 0 : Number(real);
                  const diferencia = realNum - sistema;
                  return (
                    <tr key={p._id} className="border-b last:border-0">
                      <td className="py-2 font-medium text-gray-800">{p.tipo_prenda?.nombre}</td>
                      <td className="py-2 text-center text-gray-600">{sistema}</td>
                      <td className="py-2 text-center">
                        <Input
                          type="number"
                          value={real}
                          onChange={(e) => handleChangeConteo(id, e.target.value)}
                          className="w-24 mx-auto text-center"
                        />
                      </td>
                      <td className="py-2 text-center">
                        <span className={`font-semibold ${
                          diferencia > 0 ? 'text-green-600' : diferencia < 0 ? 'text-red-600' : 'text-gray-400'
                        }`}>
                          {diferencia > 0 ? '+' : ''}{diferencia}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving || isLoading}
            className="bg-indigo-600 hover:bg-indigo-700 flex items-center gap-2"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scissors className="h-4 w-4" />}
            {isSaving ? 'Generando...' : 'Generar Corte'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CrearCorteModal;
